import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Circle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ProviderShell } from "@/components/provider/ProviderShell";
import { ReportUpload, StatusBanner, StepActions } from "@/components/provider/EvaluationSteps";
import { WeeklySpNotesPanel } from "@/components/WeeklySpNotesPanel";
import { ProviderReportingPanel } from "@/components/ProviderPortalControls";
import { ProgressNotesPanel } from "@/components/provider/ProgressNotesPanel";
import { todayET } from "@/lib/dates";

const day = (value) => (value ? new Date(value.length === 10 ? `${value}T12:00:00` : value).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");
const ENDPOINTS = { placement: "/provider-placements", task: "/client-tasks" };

export default function ProviderWorkItemPage() {
  const { itemId } = useParams();
  const [item, setItem] = useState(null);
  const [queue, setQueue] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState("");
  const load = useCallback(() => api.get(`/providers/me/work/${itemId}`).then((response) => setItem(response.data)).catch((requestError) => setError(formatApiError(requestError))), [itemId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (item?.type === "placement" && !queue) api.get("/providers/me/work-queue").then((response) => setQueue(response.data)).catch(() => setQueue(null));
  }, [item, queue]);

  async function run(request, successMessage) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const response = await request();
      if (response?.data?.id) setItem(response.data); else await load();
      if (successMessage) setNotice(successMessage);
      return true;
    } catch (requestError) {
      setError(formatApiError(requestError));
      return false;
    } finally {
      setBusy(false);
    }
  }
  const base = `/providers/me/work/${item?.id || itemId}`;
  const schedule = (date) => run(() => api.post(`${base}/schedule`, { testing_date: date }), `Testing scheduled for ${day(date)}. The school's tracker now shows “Testing scheduled”.`);
  const testingComplete = () => run(() => api.post(`${base}/testing-complete`, {}), "Testing marked complete. Upload the report when it is ready.");
  const upload = (file) => {
    const body = new FormData();
    body.append("report_file", file);
    return run(() => api.post(`${base}/report`, body, { headers: { "Content-Type": "multipart/form-data" } }), "");
  };

  const kindWord = item?.kind_word || "item";
  const today = todayET();
  const dueLabel = item?.due_date ? (item.due_date.slice(0, 10) === today ? "Due today" : item.due_date.slice(0, 10) < today && item.status !== "Completed" ? `Due ${day(item.due_date)} · overdue` : `Due ${day(item.due_date)}`) : "";
  const title = item ? (item.type === "service" ? `${item.student_initials} · ${item.service_label}` : item.service_label) : "Loading…";
  return (
    <ProviderShell back="/provider-portal/work" eyebrow={item ? `${item.kind.toUpperCase()} · ${item.client_name}` : ""} title={title} testId="provider-work-item-page">
      {error && <p className="form-message error-message" data-testid="provider-work-item-error" role="alert">{error}</p>}
      {notice && <p className="form-message provider-notice" data-testid="provider-work-item-notice" role="status">{notice}</p>}
      {item && item.type === "service" && (
        <>
          <p className="muted" data-testid="provider-work-item-meta">
            {item.grade ? `Grade ${item.grade} · ` : ""}{dueLabel || "No due date"}{item.meeting_date ? ` · Meeting ${day(item.meeting_date)}` : ""}{item.status === "Pending TAG review" || item.status === "Returned for revisions" ? "" : ` · ${item.stage}`}
          </p>
          <StatusBanner item={item} />
          <section className="provider-card provider-progress" data-testid="provider-work-progress">
            <p className="eyebrow">PROGRESS</p>
            {item.progress.map((step) => (
              <div key={step.label} className={`progress-step ${step.done ? "done" : ""}`} data-testid={`provider-progress-step-${step.done ? "done" : "open"}`}>
                {step.done ? <CheckCircle2 size={18} /> : <Circle size={18} />}
                <div><strong>{step.label}</strong>{step.at && <span>{day(step.at)}</span>}</div>
              </div>
            ))}
          </section>
          <StepActions item={item} busy={busy} onSchedule={schedule} onTestingComplete={testingComplete} />
          <ReportUpload item={item} busy={busy} onUpload={upload} onInvalid={setError} />
          {item.report?.original_filename && (
            <section className="provider-card" data-testid="provider-report-on-file">
              <p className="eyebrow">REPORT</p>
              <strong>{item.report.original_filename}</strong>
              <span className="muted">Uploaded {day(item.report.uploaded_at)}{item.report.uploaded_by_name ? ` by ${item.report.uploaded_by_name}` : ""}{item.pay ? ` · pays ${item.pay.pays_on_label}` : ""}</span>
            </section>
          )}
          <section className="provider-card" data-testid="provider-your-rate">
            <p className="eyebrow">YOUR RATE</p>
            {item.your_rate !== null && item.your_rate !== undefined ? <strong className="your-rate">{`$${Number(item.your_rate).toLocaleString("en-US", { minimumFractionDigits: 2 })}`}</strong> : <strong>Confirmed by TAG before pay</strong>}
            <span className="muted" data-testid="provider-pay-rule">{item.pay_rule}</span>
          </section>
          {item.from_school && <section className="provider-card" data-testid="provider-from-school"><p className="eyebrow">FROM THE SCHOOL</p><p>{item.from_school}</p></section>}
          <a className="ghost-button provider-message" data-testid="provider-message-tag" href={`mailto:info@theallicockgroup.com?subject=${encodeURIComponent(`${item.service_label} for ${item.student_initials} (${item.id})`)}`}>Message TAG about this {kindWord}</a>
        </>
      )}
      {item && item.type !== "service" && (
        <>
          <p className="muted" data-testid="provider-work-item-meta">{item.type === "placement" ? item.meta : dueLabel || "No due date"} · {item.status}</p>
          {item.description && <section className="provider-card" data-testid="provider-item-description"><p className="eyebrow">DETAILS</p><p>{item.description}</p></section>}
          {item.next_status && (
            <button className="primary-button" data-testid="provider-item-advance" type="button" disabled={busy} onClick={() => run(() => api.post(`${ENDPOINTS[item.type]}/${item.id}/status`, { status: item.next_status }))}>
              {item.next_status === "Acknowledged" ? "Acknowledge" : item.next_status === "In Progress" ? "Start" : "Mark completed"}
            </button>
          )}
          {item.type === "placement" && (
            <section className="provider-card" data-testid="provider-placement-actions">
              <p className="eyebrow">ACTIONS</p>
              <div className="pipeline-actions">
                <button className="ghost-button" data-testid="provider-placement-sp-notes" type="button" onClick={() => setPanel(panel === "sp-notes" ? "" : "sp-notes")}>Weekly SP notes</button>
                <button className="ghost-button" data-testid="provider-placement-progress-notes" type="button" onClick={() => setPanel(panel === "notes" ? "" : "notes")}>Progress notes</button>
                <button className="ghost-button" data-testid="provider-placement-progress-report" type="button" onClick={() => setPanel(panel === "progress" ? "" : "progress")}>Time report</button>
              </div>
            </section>
          )}
          {panel === "sp-notes" && <WeeklySpNotesPanel placements={[item]} />}
          {panel === "notes" && <ProgressNotesPanel initialClientId={item.client_id || ""} />}
          {panel === "progress" && queue && <ProviderReportingPanel queue={queue} onReload={load} />}
        </>
      )}
    </ProviderShell>
  );
}
